import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { getData, postData } from "../../../FetchApiServices";

// ---- Default guidance state ----
const DEFAULT_STATE = {
  has_seen_welcome: false,
  welcome_dismissed_at: null,
  wizard_started: false,
  wizard_completed: false,
  wizard_current_step: 0,
  wizard_workflow_id: null,
  tutorial_started: false,
  tutorial_completed: false,
  tutorial_current_step: 0,
  tutorial_workflow_id: null,
  total_workflows_created: 0,
};

// ---- Context ----
const GuidanceContext = createContext(null);

export const useGuidance = () => {
  const ctx = useContext(GuidanceContext);
  if (!ctx) return { state: DEFAULT_STATE, dismissedHints: [], loading: true, updateState: () => {}, dismissHint: () => {}, resetHints: () => {}, logEvent: () => {} };
  return ctx;
};

// ---- useHint hook — check if a hint should show ----
export const useHint = (hintId) => {
  const { dismissedHints, dismissHint } = useGuidance();
  const isDismissed = dismissedHints.includes(hintId);
  const dismiss = useCallback(() => dismissHint(hintId), [hintId, dismissHint]);
  return { shouldShow: !isDismissed, dismiss };
};

// ---- Provider component ----
const T2TGuidanceProvider = ({ children }) => {
  const [state, setState] = useState(DEFAULT_STATE);
  const [dismissedHints, setDismissedHints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hintsEnabled, setHintsEnabled] = useState(true); // session-level toggle

  // Fetch guidance state on mount
  useEffect(() => {
    const fetchState = async () => {
      try {
        const resp = await getData("t2t/guidance/state");
        if (!resp.error && resp.data?.data) {
          const d = resp.data.data;
          setState((prev) => ({
            ...prev,
            ...d,
            // Normalize dismissed hints from nested structure
          }));
          if (Array.isArray(d.dismissed_hints)) {
            setDismissedHints(d.dismissed_hints.map((h) => typeof h === "string" ? h : h.hint_id));
          }
        }
      } catch (_e) {
        // API not ready yet — use defaults
      }
      setLoading(false);
    };
    fetchState();
  }, []);

  // Update guidance state (partial update)
  const updateState = useCallback(async (updates) => {
    setState((prev) => ({ ...prev, ...updates }));
    try {
      // Fire-and-forget — optimistic update
      await postData("t2t/guidance/state", updates);
    } catch (_e) { /* API not ready */ }
  }, []);

  // Mark welcome as seen
  const markWelcomeSeen = useCallback(async () => {
    setState((prev) => ({ ...prev, has_seen_welcome: true }));
    try { await postData("t2t/guidance/welcome/seen", {}); } catch (_e) { /* */ }
  }, []);

  // Dismiss a hint permanently
  const dismissHint = useCallback(async (hintId) => {
    if (dismissedHints.includes(hintId)) return;
    setDismissedHints((prev) => [...prev, hintId]);
    try {
      await postData("t2t/guidance/hints/dismiss", { hint_id: hintId });
    } catch (_e) { /* API not ready */ }
  }, [dismissedHints]);

  // Reset all dismissed hints
  const resetHints = useCallback(async () => {
    setDismissedHints([]);
    try {
      await postData("t2t/guidance/hints/reset", {});
    } catch (_e) { /* API not ready */ }
  }, []);

  // Log analytics event
  const logEvent = useCallback(async (eventType, eventData = {}) => {
    try {
      await postData("t2t/guidance/analytics", { event_type: eventType, event_data: eventData });
    } catch (_e) { /* API not ready */ }
  }, []);

  // Should show welcome overlay?
  const shouldShowWelcome = !state.has_seen_welcome && state.total_workflows_created === 0;

  // Wizard helpers
  const startWizard = useCallback(async () => {
    setState((prev) => ({ ...prev, wizard_started: true, wizard_current_step: 1 }));
    try {
      const resp = await postData("t2t/guidance/wizard/start", {});
      if (!resp.error && resp.data?.data?.wizard_workflow_id) {
        setState((prev) => ({ ...prev, wizard_workflow_id: resp.data.data.wizard_workflow_id }));
        return resp.data.data.wizard_workflow_id;
      }
    } catch (_e) { /* */ }
    return null;
  }, []);

  const advanceWizard = useCallback(async (step, stepData = {}) => {
    setState((prev) => ({ ...prev, wizard_current_step: step }));
    try { await postData("t2t/guidance/wizard/step", { step, step_data: stepData }); } catch (_e) { /* */ }
  }, []);

  const completeWizard = useCallback(async () => {
    setState((prev) => ({ ...prev, wizard_completed: true, total_workflows_created: prev.total_workflows_created + 1 }));
    try { await postData("t2t/guidance/wizard/complete", {}); } catch (_e) { /* */ }
  }, []);

  const abandonWizard = useCallback(async (atStep) => {
    try { await postData("t2t/guidance/wizard/abandon", { step: atStep }); } catch (_e) { /* */ }
  }, []);

  // BUG-76: Replay = true restart. Zero local progress so the wizard opens at step 1.
  const resetWizardProgress = useCallback(async () => {
    setState((prev) => ({
      ...prev,
      wizard_started: false,
      wizard_current_step: 0,
      wizard_workflow_id: null,
      wizard_completed: false,
    }));
    try { await postData("t2t/guidance/wizard/abandon", { step: 0, replay: true }); } catch (_e) { /* */ }
  }, []);

  // Tutorial helpers
  const startTutorial = useCallback(async () => {
    setState((prev) => ({ ...prev, tutorial_started: true, tutorial_current_step: 1 }));
    try {
      const resp = await postData("t2t/guidance/tutorial/start", {});
      if (!resp.error && resp.data?.data?.tutorial_workflow_id) {
        setState((prev) => ({ ...prev, tutorial_workflow_id: resp.data.data.tutorial_workflow_id }));
        return resp.data.data.tutorial_workflow_id;
      }
    } catch (_e) { /* */ }
    return null;
  }, []);

  const advanceTutorial = useCallback(async (step) => {
    setState((prev) => ({ ...prev, tutorial_current_step: step }));
    try { await postData("t2t/guidance/tutorial/step", { step }); } catch (_e) { /* */ }
  }, []);

  const completeTutorial = useCallback(async () => {
    setState((prev) => ({ ...prev, tutorial_completed: true }));
    try { await postData("t2t/guidance/tutorial/complete", {}); } catch (_e) { /* */ }
  }, []);

  // BUG-76: Replay = true restart for tutorial. Zero local progress.
  const resetTutorialProgress = useCallback(async () => {
    setState((prev) => ({
      ...prev,
      tutorial_started: false,
      tutorial_current_step: 0,
      tutorial_workflow_id: null,
      tutorial_completed: false,
    }));
    try { await postData("t2t/guidance/tutorial/abandon", { step: 0, replay: true }); } catch (_e) { /* */ }
  }, []);

  // Progress milestones — computed from local state, can also be fetched from API
  const getProgress = useCallback(() => {
    const milestones = [
      { id: "welcome",    label: state.has_seen_welcome    ? "Welcome tour completed" : "Welcome tour",                             done: !!state.has_seen_welcome,    points: 10,  action: null },
      { id: "wizard",     label: state.wizard_completed    ? "First workflow created (wizard)" : "Create first workflow (wizard)",   done: !!state.wizard_completed,    points: 25,  action: "wizard" },
      { id: "tutorial",   label: state.tutorial_completed  ? "Interactive tutorial completed" : "Interactive tutorial",              done: !!state.tutorial_completed,  points: 25,  action: "tutorial" },
      { id: "template",   label: state.template_used       ? "Built a workflow from a template" : "Build a workflow from a template", done: !!state.template_used,      points: 15,  action: "templates" },
      { id: "condition",  label: state.condition_added     ? "Added a condition to a workflow" : "Add a condition to a workflow",    done: !!state.condition_added,     points: 10,  action: null },
      { id: "3workflows", label: state.total_workflows_created >= 3 ? "Published 3+ workflows" : `Publish 3+ workflows (${state.total_workflows_created || 0}/3)`, done: state.total_workflows_created >= 3, points: 15, action: null },
    ];
    const points = milestones.filter((m) => m.done).reduce((sum, m) => sum + m.points, 0);
    return { points, total: 100, percent: points, milestones };
  }, [state]);

  // Fetch progress from server (for milestones that local state may not track, e.g. template_used, condition_added)
  const refreshProgress = useCallback(async () => {
    try {
      const resp = await getData("t2t/guidance/progress");
      if (!resp.error && resp.data?.data) {
        const d = resp.data.data;
        setState((prev) => ({
          ...prev,
          template_used: d.template_used ?? prev.template_used,
          condition_added: d.condition_added ?? prev.condition_added,
          total_workflows_created: d.total_workflows_created ?? prev.total_workflows_created,
        }));
      }
    } catch (_e) { /* */ }
  }, []);

  const value = {
    state,
    loading,
    dismissedHints,
    hintsEnabled,
    setHintsEnabled,
    shouldShowWelcome,
    // Actions
    updateState,
    markWelcomeSeen,
    dismissHint,
    resetHints,
    logEvent,
    // Wizard
    startWizard,
    advanceWizard,
    completeWizard,
    abandonWizard,
    resetWizardProgress,
    // Tutorial
    startTutorial,
    advanceTutorial,
    completeTutorial,
    resetTutorialProgress,
    // Progress
    getProgress,
    refreshProgress,
  };

  return (
    <GuidanceContext.Provider value={value}>
      {children}
    </GuidanceContext.Provider>
  );
};

T2TGuidanceProvider.propTypes = { children: PropTypes.node.isRequired };

export default T2TGuidanceProvider;
